/**
 * Parceiros & Representantes — lista (mockup Fase 3).
 *
 * Representante = arquiteto, designer, broker (corretor), etc — indica clientes
 * e recebe RT (comissao). A lista mostra metricas reais calculadas no backend
 * (Indicacoes 30d + Comissao paga). Clicar numa linha abre o cockpit
 * (`/comercial/parceiros/:id`, Fase B); o lapis edita o cadastro inline.
 *
 * Reusa as classes canonicas do design system (.dtable, .entity-row,
 * .avatar-sm, .badge) + o FormPanel compartilhado para o cadastro.
 *
 * Permissoes: representante:view (lista) / :create / :edit.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiDownload, FiEdit2, FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { listarRepresentantes } from "../../../services/comercial";
import { PageContainer } from "../../../styles/shared";

import ParceiroFormPanel from "./FormPanel";
import { fmtBRLCompacto, iniciais, REDES_SOCIAIS, TIPO_LABEL } from "./shared";

export default function Parceiros() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [busca, setBusca] = useState("");
  // M-CRM-9: a lista filtra ativos por padrão. Sem este toggle, o
  // primeiro parceiro aposentado sumiria da tela sem caminho de volta.
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const [aberto, setAberto] = useState(false);
  const [parceiroEdicao, setParceiroEdicao] = useState(null);

  const podeCriar = isMaster || hasAllPermissions(["representante:create"]);
  const podeEditar = isMaster || hasAllPermissions(["representante:edit"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const payload = await listarRepresentantes(request, {
        busca, pageSize: 200, incluirInativos,
      });
      setItems(Array.isArray(payload) ? payload : payload?.results || []);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar parceiros.");
    } finally {
      setLoading(false);
    }
  }, [request, busca, incluirInativos]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const subtitulo = useMemo(() => {
    const ativos = items.filter((p) => p.ativo !== false).length;
    const arq = items.filter((p) => p.tipo === "ARQUITETO").length;
    const des = items.filter((p) => p.tipo === "DESIGNER").length;
    const brk = items.filter((p) => p.tipo === "CORRETOR").length;
    return `${ativos} ativos · ${arq} arquitetos · ${des} designers · ${brk} brokers`;
  }, [items]);

  const abrirNovo = () => {
    setParceiroEdicao(null);
    setAberto(true);
  };

  const abrirEdicao = (item) => {
    setParceiroEdicao(item);
    setAberto(true);
  };

  const fechar = () => {
    setAberto(false);
    setParceiroEdicao(null);
  };

  const aposSalvar = async () => {
    fechar();
    await carregar();
  };

  const exportarCsv = () => {
    const head = [
      "Nome", "Tipo", "Documento", "Email", "Telefone",
      ...REDES_SOCIAIS.map((r) => r.label),
      "RT %", "Indicações 30d", "Comissão paga",
    ];
    const linhas = items.map((p) => [
      p.nome, TIPO_LABEL[p.tipo] || p.tipo, p.documento || "", p.email || "",
      p.telefone || "",
      ...REDES_SOCIAIS.map((r) => p[r.key] || ""),
      Number(p.comissao_percentual || 0).toFixed(2),
      p.indicacoes_30d ?? 0, p.comissao_paga ?? "0",
    ]);
    const csv = [head, ...linhas]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "parceiros.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer style={{ maxWidth: "none" }}>
      {/* Cabeçalho */}
      <div className="flex between" style={{ alignItems: "flex-end", gap: 16, marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div className="breadcrumb">
            <span className="crumb">Início</span>
            <span className="crumb-sep">/</span>
            <span className="crumb">Comercial</span>
            <span className="crumb-sep">/</span>
            <span className="crumb active">Parceiros</span>
          </div>
          <h1 className="page-title">Parceiros &amp; Representantes</h1>
          <div className="page-sub">{loading ? "Carregando…" : subtitulo}</div>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn btn--ghost" onClick={exportarCsv} disabled={!items.length}>
            <FiDownload aria-hidden="true" /> Exportar
          </button>
          {podeCriar && (
            <button type="button" className="btn btn--primary" onClick={abrirNovo}>
              <FiPlus aria-hidden="true" /> Novo parceiro
            </button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 12, display: "flex", gap: 14, alignItems: "center" }}>
        <input
          className="input"
          style={{ maxWidth: 360 }}
          placeholder="Buscar por nome, documento ou email…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={incluirInativos}
            onChange={(e) => setIncluirInativos(e.target.checked)}
          />
          Mostrar aposentados
        </label>
      </div>

      {erro && <div className="so-banner danger" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div style={{ padding: 24, color: "var(--ink-3)" }}>Carregando…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 24, color: "var(--ink-3)" }}>
          Nenhum parceiro cadastrado.{podeCriar && " Clique em 'Novo parceiro'."}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="dtable">
            <colgroup>
              <col style={{ width: "26%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "4%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Parceiro</th>
                <th>Tipo</th>
                <th>Contato</th>
                <th className="num">RT %</th>
                <th>Indicações 30d</th>
                <th className="num">Comissão paga</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/comercial/parceiros/${p.id}`)}
                  style={{ cursor: "pointer" }}
                  title="Abrir detalhe do parceiro"
                >
                  <td>
                    <div className="entity-row">
                      <div className="avatar-sm">{iniciais(p.nome)}</div>
                      <div className="entity-meta">
                        <div className="name">{p.nome}</div>
                        <div className="sub">{p.documento || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge accent no-dot">{TIPO_LABEL[p.tipo] || p.tipo}</span>
                  </td>
                  <td>
                    <span className="mono">{p.email || "—"}</span>
                    <div className="tiny muted">{p.telefone || "—"}</div>
                  </td>
                  <td className="num">{Number(p.comissao_percentual || 0).toFixed(0)}%</td>
                  <td>
                    <strong>{p.indicacoes_30d ?? 0}</strong>{" "}
                    <span className="muted">· {fmtBRLCompacto(p.indicacoes_valor)}</span>
                  </td>
                  <td className="num">{fmtBRLCompacto(p.comissao_paga)}</td>
                  <td>
                    {podeEditar && (
                      <button
                        type="button"
                        className="icon-btn"
                        title="Editar cadastro"
                        aria-label="Editar cadastro"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirEdicao(p);
                        }}
                      >
                        <FiEdit2 size={15} aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sidebar de cadastro/edicao (compartilhada com o cockpit) */}
      <ParceiroFormPanel
        open={aberto}
        parceiro={parceiroEdicao}
        onClose={fechar}
        onSaved={aposSalvar}
      />
    </PageContainer>
  );
}
